"use client";

import { usePathname, useRouter } from "@/lib/navigation";
import { PermissionGate, SettingsLayout, useEntitlements } from "@repo/ui";
import { useMessages } from "@/locales";
import { useSession } from "@repo/api-client";

export default function SettingsSectionLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const zhCN = useMessages();

  const router = useRouter();
  const pathname = usePathname();
  const { user } = useSession();
  const { included } = useEntitlements();
  const isPlatformAdmin =
    user?.permissions.includes("platform:manage_entitlements") ?? false;

  const navItems = [
    {
      id: "profile",
      label: zhCN.settings.profile,
      href: "/settings",
      active: pathname === "/settings",
      section: zhCN.settings.personal,
    },
    {
      id: "password",
      label: zhCN.settings.password,
      href: "/settings/password",
      active: pathname.startsWith("/settings/password"),
      section: zhCN.settings.personal,
    },
    {
      id: "security",
      label: zhCN.settings.security,
      href: "/settings/security",
      active: pathname.startsWith("/settings/security"),
      section: zhCN.settings.personal,
    },
    {
      id: "sessions",
      label: zhCN.settings.sessions,
      href: "/settings/sessions",
      active: pathname.startsWith("/settings/sessions"),
      section: zhCN.settings.personal,
    },
    ...(included("feature.module.notification") ? [{
      id: "notifications",
      label: zhCN.settings.notifications,
      href: "/settings/notifications",
      active: pathname.startsWith("/settings/notifications"),
      section: zhCN.settings.personal,
    }] : []),
    {
      id: "organization",
      label: zhCN.settings.organization,
      href: "/settings/organization",
      active: pathname.startsWith("/settings/organization"),
      section: zhCN.settings.org,
    },
    {
      id: "branches",
      label: zhCN.settings.branches,
      href: "/settings/branches",
      active: pathname.startsWith("/settings/branches"),
      section: zhCN.settings.org,
    },
    {
      id: "audit",
      label: zhCN.settings.audit,
      href: "/settings/audit",
      active: pathname.startsWith("/settings/audit"),
      section: zhCN.settings.org,
    },
    {
      id: "invitations",
      label: zhCN.settings.invitations,
      href: "/settings/invitations",
      active: pathname.startsWith("/settings/invitations"),
      section: zhCN.settings.org,
    },
    {
      id: "api-keys",
      label: zhCN.settings.apiKeys,
      href: "/settings/api-keys",
      active: pathname.startsWith("/settings/api-keys"),
      section: zhCN.settings.developer,
    },
    {
      id: "webhooks",
      label: zhCN.settings.webhooks,
      href: "/settings/webhooks",
      active: pathname.startsWith("/settings/webhooks"),
      section: zhCN.settings.developer,
    },
    ...(isPlatformAdmin
      ? [{
          id: "platform",
          label: zhCN.settings.platform,
          href: "/settings/platform",
          active: pathname.startsWith("/settings/platform"),
          section: zhCN.settings.platform,
        }]
      : []),
  ];

  return (
    <PermissionGate permission="setting:read">
      <SettingsLayout
        title={zhCN.settings.title}
        subtitle={zhCN.settings.description}
        navItems={navItems}
        onNavigate={(href) => router.push(href)}
      >
        {children}
      </SettingsLayout>
    </PermissionGate>
  );
}
